import {
  Skeleton as MantineSkeleton,
  Stack,
  type SkeletonProps as MantineSkeletonProps,
} from '@mantine/core';

export interface ForgeSkeletonProps
  extends Omit<MantineSkeletonProps, 'visible' | 'width' | 'height' | 'animate'> {
  /** Number of skeleton lines to render. Defaults to 1. */
  lines?: number;
  /** Width of each line. Number → pixels, string → CSS value. */
  width?: number | string;
  /** Height of each line. Number → pixels. Defaults to 12. */
  height?: number;
  /** Gap between lines when `lines > 1`. Defaults to 8. */
  gap?: number;
  /** Toggle the shimmer animation. */
  animate?: boolean;
}

/**
 * Skeleton — loading placeholder rectangles.
 *
 * Prefer skeletons over spinners for any list / card / table while data
 * fetches — PLAN.md mandates "skeleton screens (not spinners) for all
 * data-loaded surfaces".
 */
export function Skeleton({
  lines = 1,
  width = '100%',
  height = 12,
  gap = 8,
  animate = true,
  radius = 'sm',
  ...rest
}: ForgeSkeletonProps) {
  if (lines <= 1) {
    return (
      <MantineSkeleton
        height={height}
        width={width}
        radius={radius}
        animate={animate}
        {...rest}
      />
    );
  }

  return (
    <Stack gap={gap}>
      {Array.from({ length: lines }).map((_, index) => (
        <MantineSkeleton
          key={index}
          height={height}
          width={index === lines - 1 ? '70%' : width}
          radius={radius}
          animate={animate}
          {...rest}
        />
      ))}
    </Stack>
  );
}
